import React, { useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import { useTheme } from "../../../context/ThemeContext";

/**
 * Collapsible filter panel for Cousin Lookup.
 * Lets users narrow the Person A / Person B dropdowns by generation and birth era.
 */
function FilterPanel({
  familyTreeData,
  selectedGenerations,
  onGenerationsChange,
  selectedEras,
  onErasChange,
}) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [expanded, setExpanded] = useState(false);

  // Build sorted list of unique generations found in the data (e.g. "01", "02", …)
  const generationOptions = useMemo(() => {
    if (!familyTreeData) return [];
    const genSet = new Set();
    familyTreeData.forEach((p) => {
      if (p.data.generation) genSet.add(p.data.generation);
    });
    return Array.from(genSet).sort((a, b) => Number(a) - Number(b));
  }, [familyTreeData]);

  // Build birth-era ranges in 25-year buckets computed from actual birth years.
  // Also includes "Unknown" for people with no parseable birth year.
  const eraOptions = useMemo(() => {
    if (!familyTreeData) return [];

    let minYear = Infinity;
    let maxYear = -Infinity;
    let hasUnknown = false;

    familyTreeData.forEach((p) => {
      const year = parseBirthYear(p.data.birthday);
      if (year === null) {
        hasUnknown = true;
      } else {
        if (year < minYear) minYear = year;
        if (year > maxYear) maxYear = year;
      }
    });

    if (minYear === Infinity) return hasUnknown ? ["Unknown"] : [];

    // Round down to nearest 25 for the starting boundary
    const start = Math.floor(minYear / 25) * 25;
    // Round up to nearest 25 for the ending boundary
    const end = Math.ceil((maxYear + 1) / 25) * 25;

    const ranges = [];
    for (let y = start; y < end; y += 25) {
      ranges.push(`${y}\u2013${y + 25}`);
    }
    if (hasUnknown) ranges.push("Unknown");
    return ranges;
  }, [familyTreeData]);

  const hasActiveFilters =
    selectedGenerations.length > 0 || selectedEras.length > 0;

  const handleClear = () => {
    onGenerationsChange([]);
    onErasChange([]);
  };

  return (
    <div className="flex flex-col gap-y-[8px]">
      {/* Toggle button */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="text-[14px] text-dupontBlue dark:text-dupontGold hover:underline self-start flex items-center gap-x-[4px]"
      >
        <span
          className="inline-block transition-transform"
          style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
        >
          ▶
        </span>
        Filters
        {hasActiveFilters && (
          <span className="ml-[4px] text-[12px] bg-dupontGold text-white rounded-full px-[6px] py-[1px]">
            {selectedGenerations.length + selectedEras.length}
          </span>
        )}
      </button>

      {/* Filter dropdowns (visible when expanded) */}
      {expanded && (
        <div className="flex flex-col gap-y-[12px] p-[12px] rounded-[12px] border border-gray-200 dark:border-dupontGold/30 bg-gray-50 dark:bg-darkCard">
          <div className="flex flex-row gap-x-[12px] flex-wrap gap-y-[12px]">
            {/* Generation multi-select */}
            <Autocomplete
              multiple
              size="small"
              options={generationOptions}
              value={selectedGenerations}
              onChange={(_, val) => onGenerationsChange(val)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  return (
                    <Chip key={key} label={`Gen ${option}`} size="small" {...rest} />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Generation"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: isDark ? "#1e293b" : "white",
                      borderRadius: "8px",
                      color: isDark ? "#e2e8f0" : undefined,
                    },
                    "& .MuiInputLabel-root": {
                      color: isDark ? "#b8860b" : undefined,
                      "&.Mui-focused": { color: "#b8860b" },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isDark ? "rgba(184, 134, 11, 0.4)" : undefined,
                    },
                    "& .MuiSvgIcon-root": {
                      color: isDark ? "#94a3b8" : undefined,
                    },
                  }}
                />
              )}
              sx={{ minWidth: 200, flex: 1 }}
            />

            {/* Birth era multi-select */}
            <Autocomplete
              multiple
              size="small"
              options={eraOptions}
              value={selectedEras}
              onChange={(_, val) => onErasChange(val)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  return <Chip key={key} label={option} size="small" {...rest} />;
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Birth Era"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: isDark ? "#1e293b" : "white",
                      borderRadius: "8px",
                      color: isDark ? "#e2e8f0" : undefined,
                    },
                    "& .MuiInputLabel-root": {
                      color: isDark ? "#b8860b" : undefined,
                      "&.Mui-focused": { color: "#b8860b" },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isDark ? "rgba(184, 134, 11, 0.4)" : undefined,
                    },
                    "& .MuiSvgIcon-root": {
                      color: isDark ? "#94a3b8" : undefined,
                    },
                  }}
                />
              )}
              sx={{ minWidth: 200, flex: 1 }}
            />
          </div>

          {/* Clear button */}
          {hasActiveFilters && (
            <button
              onClick={handleClear}
              className="text-[13px] text-red-500 hover:underline self-start"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Extract the 4-digit year from a birthday string like "15 June 1950" or "1825". */
export function parseBirthYear(birthday) {
  if (!birthday) return null;
  const tokens = birthday.trim().split(/\s+/);
  for (let i = tokens.length - 1; i >= 0; i--) {
    const n = Number(tokens[i]);
    if (!isNaN(n) && n >= 1000 && n <= 2100) return n;
  }
  return null;
}

export default FilterPanel;
